import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subject, fromEvent, merge, of } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';
import { ActivityLog } from '../typings';
import { Constants } from '../constants';
import { SharedService } from './shared.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ActivityLoggerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private connectionStatus$ = new BehaviorSubject<boolean>(navigator.onLine);
  private logQueue: ActivityLog[] = [];
  private sendIntervalId?: number;
  private batteryLevelChangeHandler?: () => void;
  private batteryChargingChangeHandler?: () => void;
  private battery?: any;
  
  private readonly API_ENDPOINT = '/api/activity-logs';
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly SEND_INTERVAL = 30000; // 30 seconds
  private readonly STORAGE_KEY = 'activity_log_queue';

  constructor(private http: HttpClient, private sharedService: SharedService, private authService: AuthService) {
    this.loadQueueFromStorage();
    this.initializeConnectionMonitoring();
    this.initializeErrorMonitoring();
    this.startPeriodicSending();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.sendIntervalId) {
      clearInterval(this.sendIntervalId);
    }
    // Remove battery event listeners
    if (this.battery) {
      if (this.batteryLevelChangeHandler) {
        this.battery.removeEventListener('levelchange', this.batteryLevelChangeHandler);
      }
      if (this.batteryChargingChangeHandler) {
        this.battery.removeEventListener('chargingchange', this.batteryChargingChangeHandler);
      }
    }
    this.sendQueuedLogs();
  }

  /**
   * Initialize connection monitoring
   */
  private initializeConnectionMonitoring(): void {
    const online$ = fromEvent(window, 'online');
    const offline$ = fromEvent(window, 'offline');
    
    merge(online$, offline$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConnectionStatus();
        this.logConnectionChange();
      });

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      fromEvent(connection, 'change')
        .pipe(
          debounceTime(10000),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateConnectionStatus();
        });
    }
    this.checkBatteryLevel();
    this.updateConnectionStatus();
  }

  /**
   * Initialize global error monitoring
   */
  private initializeErrorMonitoring(): void {
    fromEvent<ErrorEvent>(window, 'error')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        console.log("javascript error", JSON.stringify(event));
        this.logError('javascript_error', JSON.stringify(event));
      });
  }

  /** Check battery level, log activity if changing and log error if less than 50% */
  private checkBatteryLevel(): void {
    if ('getBattery' in navigator) {
      console.log("Battery API is supported");
      (navigator as any).getBattery().then((battery: any) => {
        this.battery = battery;
        let lastLevel = battery.level;
        console.log(`Initial battery level: ${battery.level * 100}%`);
        
        this.batteryLevelChangeHandler = () => {
          console.log(`Battery level changed: ${battery.level * 100}%`);
          if (Math.abs(battery.level - lastLevel) > 0.1) {
            lastLevel = battery.level;
            this.logActivity('battery_level_change', `Battery level changed to ${battery.level * 100}%`);  

            if (battery.level < 0.5) {
              this.logError('battery_level_low', `Battery level is low: ${battery.level * 100}%`);
            }
          }
        };
        
        this.batteryChargingChangeHandler = () => {
          console.log(`Battery level changed: ${battery.level * 100}%`);
          if (Math.abs(battery.level - lastLevel) > 0.1) {
            lastLevel = battery.level;
            this.logActivity('battery_level_change', `Battery level charged to ${battery.level * 100}%, time to full charge: ${battery.chargingTime}s`);  
          }
        };
        
        battery.addEventListener('levelchange', this.batteryLevelChangeHandler);
        battery.addEventListener('chargingchange', this.batteryChargingChangeHandler);
      });
    }
    else{
      console.log("Battery API is not supported on this browser.");
      this.logActivity('battery_level_change', `Battery API is not supported on this browser.`);  
    }
  }

  /**
   * Update connection status
   */
  private updateConnectionStatus(): void {
    const status= navigator.onLine;
    this.connectionStatus$.next(status);
  }

  /**
   * Log connection changes
   */
  private logConnectionChange(): void {
    const status = this.connectionStatus$.value;
    this.logActivity('connection_change', status ? 'online' : 'offline');
  }

  /**
   * Log general activity
   */
  logActivity(event: string, details: string): void {
    this.sharedService.getToken().then(token => {
      const log: ActivityLog = {
        token: token ?? '',
        timestamp: new Date(),
        type: 'activity',
        event,
        details,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      this.addToQueue(log);
    });
  }

  /**
   * Log errors
   */
  logError(event: string, details: string): void {
    this.sharedService.getToken().then(token => {
      const log: ActivityLog = {
        token: token ?? '',
        timestamp: new Date(),
        type: 'error',
        event,
        details,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      this.addToQueue(log);
      if (this.connectionStatus$.value) {
        this.sendQueuedLogs();
      }
    });
  }

  /**
   * Log application start
   */
  logAppStart(): void {
    this.logActivity('app_started', '');
  }

  /**
   * Add log to queue
   */
  private addToQueue(log: ActivityLog): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const queue: ActivityLog[] = stored ? JSON.parse(stored) : [];
      queue.push(log);
      
      if (queue.length > this.MAX_QUEUE_SIZE) {
        queue.splice(0, queue.length - this.MAX_QUEUE_SIZE);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Failed to save log to localStorage:', error);
    }
  }

  /**
   * Start periodic sending of logs
   */
  private startPeriodicSending(): void {
    this.sendIntervalId = setInterval(() => {
      console.log("Periodic log sending triggered");
      this.loadQueueFromStorage();
      if (this.connectionStatus$.value && this.logQueue.length > 0) {
        this.sendQueuedLogs();
      }
    }, this.SEND_INTERVAL) as any;
  }

  /**
   * Send queued logs to backend
   */
  private sendQueuedLogs(): void {
    if (this.logQueue.length === 0) {
      return;
    }

    const logsToSend = [...this.logQueue];

    this.http.post(this.API_ENDPOINT, { logs: logsToSend })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Failed to send logs to backend:', error);
          // Ajouter un log d'échec dans localStorage
          const failureLog: ActivityLog = {
            token: localStorage.getItem(Constants.LocalStorage.ComEntToken) ?? '',
            timestamp: new Date(),
            type: 'error',
            event: 'log_send_failed',
            details: `Failed to send ${logsToSend.length} logs: ${error.message}`,
            userAgent: navigator.userAgent,
            url: window.location.href,
          };
          try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            const queue: ActivityLog[] = stored ? JSON.parse(stored) : [];
            queue.push(failureLog);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(queue));
          } catch (e) {
            console.error('Failed to log send failure:', e);
          }
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response !== null) {
          // Succès : vider localStorage
          try {
            this.clearQueue();
            console.log(`Successfully sent ${logsToSend.length} logs`);
          } catch (error) {
            console.error('Failed to clear localStorage:', error);
          }
        }
      });
  }

  /**
   * Manual log sending trigger
   */
  sendLogs(): void {
    this.sendQueuedLogs();
  }

  /**
   * Clear log queue
   */
  clearQueue(): void {
    this.logQueue = [];
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.logQueue = JSON.parse(stored);
        console.log(`Loaded ${this.logQueue.length} logs from localStorage`);
      } else {
        this.logQueue = [];
      }
    } catch (error) {
      console.error('Failed to load logs from localStorage:', error);
      this.logQueue = [];
    }
  }
}